import { InfoCircle } from '@untitledui-pro/icons/line';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { getGrid } from '../../../../store/LoadingStore';
import { computeParam1, computeParam2 } from '../../../../Utils/UtilityFunctions';
import { formatMetricNumber, formatNumberWithApostrophes } from '../../../../Utils/MetricFormatting';
import { SensorData } from '../../../../Simulations/SensorPoint';
import usePersistentStore from '../../../store/persistent/persistentStore';
import { ControllerManager } from '@gui/controller/ControllerManager';
import CustomTooltip from '@gui/components/generic/CustomTooltip';

// UI-only: parent provides values/functions via props.
export type ActiveSensorAnalyticsProps = {
    activeSensorID: number;
    detailsOpen: boolean;
    onToggleDetails: () => void;
};

type SensorDetailsRow = {
    label: string;
    original: string;
    actual: string;
};

type SensorMetaRow = {
    label: string;
    value: string;
};

type SensorNeighbourKey = 'neighbors6' | 'neighbors26';

const LABEL_COLUMN_WIDTH = '88px';
const VALUE_OFFSET_WIDTH = '8px';

const formatActiveBelowValue = (value: number) =>
    formatNumberWithApostrophes(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

const countActiveNeighbours = (sensorData: SensorData, sensorCollection: SensorData[], neighbourKey: SensorNeighbourKey) => {
    const activeIds = new Set(sensorCollection.map((sensor) => sensor.getID()));
    return sensorData.getSensorPoint()[neighbourKey].filter((neighbour) => activeIds.has(neighbour.getID())).length;
};

const ActiveSensorAnalytics: React.FC<ActiveSensorAnalyticsProps> = ({ activeSensorID, detailsOpen, onToggleDetails }) => {
    const pipelineExecutionCount = usePersistentStore((state) => state.getActiveLayerState().pipeline.executionCount);
    const pipelineController = React.useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const { originalSensor, actualSensor, originalSensors, actualSensors } = React.useMemo(() => {
        const frameSensors = getGrid().getActiveLayer().getActiveFrame().getSensors();
        const original = frameSensors.find((sensor) => sensor.getID() === activeSensorID) ?? frameSensors[activeSensorID];

        // Get sensor value as the output of the pipeline:
        const pipelineResult = pipelineController.getLastResult();
        const pipelinedSensor = pipelineResult?.data?.find((sensor) => sensor.getID() === activeSensorID);
        const pipelineSensors = pipelineResult?.data ?? frameSensors;

        return {
            originalSensor: original,
            actualSensor: pipelinedSensor ?? original,
            originalSensors: frameSensors,
            actualSensors: pipelineSensors,
        };
    }, [activeSensorID, pipelineExecutionCount, pipelineController]);

    if (!originalSensor || !actualSensor) return null;

    const formatPosition = (sensorData: typeof actualSensor) => {
        const { x, y, z } = sensorData.getPosition();
        return `[${formatMetricNumber(x)}, ${formatMetricNumber(y)}, ${formatMetricNumber(z)}]`;
    };

    const getSensorRows = (sensorData: typeof actualSensor, sensorCollection: typeof actualSensors): Record<string, string> => ({
        id: formatMetricNumber(sensorData.getID()),
        index: formatMetricNumber(sensorData.getSensorPoint().index),
        position: formatPosition(sensorData),
        level: formatMetricNumber(sensorData.getSensorPoint().getFloor()),
        neighbors6: formatMetricNumber(countActiveNeighbours(sensorData, sensorCollection, 'neighbors6')),
        neighbors26: formatMetricNumber(countActiveNeighbours(sensorData, sensorCollection, 'neighbors26')),
        value: formatMetricNumber(sensorData.getScalarValue()),
        theta: formatMetricNumber(computeParam1(sensorData.getVectorialResult())),
        phi: formatMetricNumber(computeParam2(sensorData.getVectorialResult())),
        neighbors: formatActiveBelowValue(sensorData.computeNeighboursScore(0, sensorCollection) || 0),
    });

    const originalValues = getSensorRows(originalSensor, originalSensors);
    const actualValues = getSensorRows(actualSensor, actualSensors);

    const metaRows: SensorMetaRow[] = [
        { label: 'ID', value: originalValues.id },
        { label: 'Index', value: originalValues.index },
        { label: 'Level', value: originalValues.level },
        { label: 'Position', value: originalValues.position },
    ];

    const comparisonRows: SensorDetailsRow[] = [
        { label: 'Value', original: originalValues.value, actual: actualValues.value },
        { label: 'Dir theta', original: originalValues.theta, actual: actualValues.theta },
        { label: 'Dir phi', original: originalValues.phi, actual: actualValues.phi },
        { label: '6 neighb.', original: originalValues.neighbors6, actual: actualValues.neighbors6 },
        { label: '26 neighb.', original: originalValues.neighbors26, actual: actualValues.neighbors26 },
        { label: 'Active neighbors', original: originalValues.neighbors, actual: actualValues.neighbors },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box style={{ marginBottom: '-5px', display: 'flex', alignItems: 'center' }}>
                Selected
                <IconButton
                    sx={{ ml: 0.2 }}
                    onClick={onToggleDetails}
                    aria-pressed={detailsOpen}
                    aria-expanded={detailsOpen}
                    aria-label="Toggle active sensor analytics"
                >
                    <CustomTooltip
                        side="left"
                        title={
                            <>
                                Click here for more info on this sensor.
                                <br />
                                Ctrl+Click a sensor in the scene to select it.
                                <br />
                            </>
                        }
                        placement="top"
                    >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                            <InfoCircle className="icon-size-medium" />
                        </Box>
                    </CustomTooltip>
                </IconButton>
            </Box>

            {detailsOpen ? (
                <>
                    <Box
                        sx={{
                            pl: 0.1,
                            mb: 0.75,
                            display: 'grid',
                            gridTemplateColumns: `${LABEL_COLUMN_WIDTH} ${VALUE_OFFSET_WIDTH} minmax(0, 1fr)`,
                            columnGap: 1.5,
                            rowGap: 0.25,
                            alignItems: 'start',
                        }}
                    >
                        {metaRows.map((row) => (
                            <React.Fragment key={row.label}>
                                <Typography variant="body2" component="div" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                    {row.label}
                                </Typography>
                                <Box />
                                <Typography variant="body2" component="div" sx={{ gridColumn: 3 }}>
                                    {row.value}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            pl: 0.1,
                            mb: 1,
                            display: 'grid',
                            gridTemplateColumns: `${LABEL_COLUMN_WIDTH} ${VALUE_OFFSET_WIDTH} minmax(0, 1fr) minmax(0, 1fr)`,
                            columnGap: 1.5,
                            rowGap: 0.25,
                            alignItems: 'start',
                        }}
                    >
                        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }} />
                        <Box />
                        <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                            Original
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ fontWeight: 600, ml: 1 }}>
                            Actual
                        </Typography>

                        {comparisonRows.map((row) => (
                            <React.Fragment key={row.label}>
                                <Typography variant="body2" component="div" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                    {row.label}
                                </Typography>
                                <Box />
                                <Typography variant="body2" component="div">
                                    {row.original}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ ml: 1 }}>
                                    {row.actual}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Box>
                </>
            ) : null}
        </Box>
    );
};

export default ActiveSensorAnalytics;
